import { useEffect } from "react";
import { AuthProvider as OidcAuthProvider } from "react-oidc-context";
import './App.css';
import AppContainer from './AppContainer';
import { AuthProvider } from './providers/AuthProvider';
import ErrorBoundary from './components/ErrorBoundary';
import ToastProvider from './components/ToastProvider';
import NewVersionBanner from './components/NewVersionBanner';
import { useConfigContext } from "./providers/ConfigProvider";
import { oidcUserStore } from "./helpers/oidcStorage";
import type { User } from "oidc-client-ts";

function resolveOidcBase(): string {
    // Build-time env var takes priority
    const envBase = import.meta.env.VITE_BASE_PATH as string | undefined;
    if (envBase) return envBase.endsWith('/') ? envBase : `${envBase}/`;

    // Legacy runtime detection
    const p = window.location.pathname;
    if (p.startsWith('/v')) return '/' + p.split('/')[1] + '/';
    if (p.startsWith('/beta')) return '/beta/';
    return '';
}

/**
 * Etter BankID lander søkeren på redirect_uri med ?code&state i adressen.
 * `login()` i AuthProvider legger siden hen startet på i state; her byttes
 * adressen til den, før ruteren mounter (AuthProvider viser laster så lenge
 * innloggingen behandles). Uten returnTo, eller med noe som ikke er en sti på
 * samme domene, fjernes bare ?code&state — da går «/» videre til Din side.
 */
function returnAfterSignin(user: User | void): void {
    const returnTo = (user?.state as { returnTo?: unknown } | undefined)?.returnTo;
    const isLocalPath = typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//');
    window.history.replaceState({}, document.title, isLocalPath ? returnTo : window.location.pathname);
}


const App = () => {
    const base = resolveOidcBase();
    const {data} = useConfigContext();

    const oidcConfig = {
        authority: import.meta.env.VITE_OIDC_AUTHORITY,
        client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
        client_secret: import.meta.env.VITE_OIDC_CLIENT_SECRET,
        redirect_uri: `${window.location.origin}${base}`,
        post_logout_redirect_uri: window.location.origin,
        scope: "openid profile nin email",
        // localStorage, så innloggingen gjelder i alle faner — se helpers/oidcStorage.ts
        userStore: oidcUserStore,
        onSigninCallback: returnAfterSignin
    };

    // Stiftelsens eget tema. Azure-funksjonen legger inn de samme lenkene når
    // den kjenner stiftelsen fra hostnavnet; dette dekker tilfellene der
    // stiftelsen først blir kjent i klienten. Må være <link>, ikke import():
    // en CSS-fil kan ikke lastes som JS-modul, og den feilede importen utløste
    // vite:preloadError — og dermed reload-vakten i main.tsx.
    useEffect(() => {
        // Lokalt importerer main.tsx kopiene i src/css/ — de skal ikke overstyres
        const entityPath = data?.entity_path;
        if (!entityPath || import.meta.env.DEV) return;

        for (const file of ['bootstrap-custom.min.css', 'app.custom.css']) {
            const href = new URL(`${entityPath}/css/${file}`, window.location.href).href;
            const exists = [...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')]
                .some(link => link.href === href);
            if (exists) continue;

            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            // Stiftelsen mangler fila (404): fjern lenka i stedet for å la den henge
            link.onerror = () => link.remove();
            document.head.appendChild(link);
        }
    }, [data?.entity_path]);

    return (
        <ErrorBoundary>
            <NewVersionBanner />
            <ToastProvider>
                <OidcAuthProvider {...oidcConfig}>
                    <AuthProvider>
                        <AppContainer />
                    </AuthProvider>
                </OidcAuthProvider>
            </ToastProvider>
        </ErrorBoundary>
    );
};

export default App